import React, { useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Button } from '@mui/material';

import Feedback from 'components/Dialog/Feedback';
import ExitSession from 'components/Dialog/ExitSession';
import { Wrapper, Container } from './styled';
import {
  SessionCountdown,
  Overview,
  Header,
  DeliveryAddress,
  AccessCode,
  Instructions,
  DropoffPhoto,
  ExpiredToken,
  Backdrop,
} from 'components/DeliveryManager';

import api from 'utils/api';
import { getCookie } from 'utils/cookie';
import AccommodationsEmail from 'components/AccommodationsEmail';

const PREFIX = 'ManageDeliveryContainer';

const classes = {
  root: `${PREFIX}-root`,
  container: `${PREFIX}-container`,
  submit: `${PREFIX}-submit`,
  redirect: `${PREFIX}-redirect`,
  expired: `${PREFIX}-expired`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {
    borderRadius: '2rem',
    fontWeight: 700,
    borderWidth: '2px !important',
    borderColor: theme.palette.primary.main,
  },

  [`& .${classes.container}`]: {
    color: theme.palette.common12,
    padding: '1rem 1.5rem',
    [theme.breakpoints.up('sm')]: {
      padding: '1rem 3rem',
    },
    border: `0.3px solid ${theme.colorTokens.outlineVariant}`,
    borderTop: 'none',
    backgroundColor: theme.colorTokens.surfaceContainerLowest,
  },

  [`& .${classes.submit}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },

  [`& .${classes.redirect}`]: {
    color: theme.palette.common10,
    fontSize: '14px',
    display: 'block',
    marginTop: '1rem',
    textAlign: 'center',

    '& > a': {
      color: theme.colorTokens.hyperlink,
      padding: '0 2px',
    },
  },

  [`& .${classes.expired}`]: {
    width: '200px',
    borderRadius: '2rem',
  },
}));

export const ManageDeliveryContainer = compose(
  inject('store'),
  observer,
)(({ store }) => {
  const navigate = useNavigate();
  const { trackingCode } = useParams();
  const [openDialog, setOpenDialog] = useState(false);

  const { handleSubmit, control, setValue, formState, watch } = useForm({
    defaultValues: { access_code_map: [], delivery_instruction: '', image: [] },
    shouldFocusError: true,
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'access_code_map' });
  const { deliveryStore } = store;
  const { token, delivery, loadingDelivery } = deliveryStore;
  const { access_code_map, preferred_photo, delivery_instruction } = delivery || {};

  const { errors } = formState;
  const watchAccessCodes = watch('access_code_map');
  const controlledAccessCodes = fields.map((field, index) => ({ ...field, ...watchAccessCodes[index] }));

  const renderInstructions = useCallback(
    ({ field }) => <Instructions {...field} errors={errors} />,
    [errors],
  );

  const renderDropoffPhoto = useCallback(({ field }) => <DropoffPhoto {...field} />, []);

  const onSubmit = (data) => {
    const accessCodeMap = data.access_code_map
      .filter((item) => item && typeof item.type === 'string' && item.type.trim() !== '')
      .map((item) => ({ [item.type]: item.code }));
    const filteredCode = _.merge({}, ...accessCodeMap);
    // no-dd-sa:javascript-code-style/assignment-name
    const access_code_map = Object.keys(filteredCode).length > 0 ? JSON.stringify(filteredCode) : '';

    let params = { ...data, access_code_map };
    params = _.omit(params, 'image');

    const formData = new FormData();
    for (const key in params) {
      formData.append(key, params[key]);
    }

    const uploadImages = data.image.filter((img) => img.url && typeof img.url !== 'string');
    const uploadedImages = data.image
      .filter((img) => typeof img.url === 'string')
      .map((img) => img.url.split('?')[0])
      .filter(Boolean);

    formData.append('image_urls', uploadedImages.toString());
    uploadImages.forEach((image) => formData.append('image', image.url));

    api
      .post(`delivery/${trackingCode}/${delivery.shipment.id}/update`, formData, { headers: { 'Content-Type': undefined } })
      .then((response) => {
        if (response.status === 200) {
          navigate(`/${trackingCode}/sessions/completed`);
        } else {
          toast.error(<span style={{ color: '#fff', fontSize: '14px', fontWeight: 500 }}>Something went wrong. Please try again!</span>, {
            theme: 'colored',
            hideProgressBar: true,
          });
        }
      })
      .catch((error) => {
        toast.error(<span style={{ color: '#fff', fontSize: '14px', fontWeight: 500 }}>Something went wrong. Please try again!</span>, {
          theme: 'colored',
          hideProgressBar: true,
        });
      });
  };

  const handleConfirm = () => {
    setOpenDialog(true);
    api.post(`/delivery/${trackingCode}/${delivery.shipment.id}/confirm`);
  };

  useEffect(() => {
    const token = getCookie('axl_shipment_token');
    if (!token) return;

    deliveryStore.getTokenInfo(token, () => {
      deliveryStore.getDelivery(trackingCode, () => {
        deliveryStore.getAccessCodeTypes();
        deliveryStore.markManageDeliveryOpened();
      });
    });
  }, [deliveryStore, trackingCode]);

  useEffect(() => {
    if (access_code_map) {
      const codes = Object.keys(access_code_map)
        .map((type) => ({ type, code: access_code_map[type] }))
        .filter(({ code }) => Boolean(code));
      setValue('access_code_map', codes);
    }
    if (preferred_photo) setValue('image', preferred_photo);
    if (delivery_instruction) setValue('delivery_instruction', delivery_instruction);
  }, [append, access_code_map, preferred_photo, delivery_instruction, remove, setValue]);

  if (!token) return <ExpiredToken />;

  if (loadingDelivery) return <Backdrop />;

  return (
    <Root>
      <Wrapper>
        <Container>
          <SessionCountdown />
          <div className={classes.container}>
            <Overview />
            <div style={{ margin: '1.5rem 0', height: '1px', backgroundColor: '#cccccc' }} />
            <form onSubmit={handleSubmit(onSubmit)}>
              <Header />
              <DeliveryAddress />
              <AccessCode
                fields={fields}
                append={append}
                remove={remove}
                control={control}
                controlledAccessCodes={controlledAccessCodes}
                errors={errors}
              />
              <Controller
                name="delivery_instruction"
                control={control}
                rules={{ minLength: { value: 3, message: 'This field should contain at least 3 characters' } }}
                render={renderInstructions}
              />
              <Controller name="image" control={control} render={renderDropoffPhoto} />
              <div className={classes.submit}>
                <Button type="submit" fullWidth variant="contained" color="primary" classes={{ root: classes.root }} disableElevation>
                  Save
                </Button>
                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  color="primary"
                  classes={{ root: classes.root }}
                  onClick={handleConfirm}
                  disableElevation
                >
                  This looks great!
                </Button>
              </div>
              <span className={classes.redirect}>
                Click <Link to={`/${trackingCode}/edit`}>here</Link> to access all Delivery Detail sections.
              </span>
              <AccommodationsEmail fontSize={14} />
            </form>
          </div>
        </Container>
        {openDialog && <Feedback open={openDialog} onCancel={() => setOpenDialog(false)} />}
        <ExitSession />
      </Wrapper>
    </Root>
  );
});
